import React, { FunctionComponent, useEffect, useRef, useState, useCallback } from "react"
import { useDispatch, useSelector } from "react-redux"
import { ApplicationState } from "../../core/types"
import { WhatsNewState } from "../../data/stores/whats-new/whats-new-types"
import { setWhatsNewOpen } from "../../data/stores/whats-new/whats-new-actions"
import { NewsFeedItem } from "../../data/model/whats-new-model"
import { WhatsNewFeedItem } from "./WhatsNewFeedItem"
import { WhatsNewSkeletons } from "./WhatsNewSkeletons"

const PAGE_SIZE = 10
// Configured maximum number of feed items rendered, regardless of how many the feed returns
// (SRD "Limits and resiliency"). Client-side windowing pages through up to this many.
const MAX_FEED_ITEMS = 50

export const WhatsNewDrawer: FunctionComponent = () => {
    const dispatch = useDispatch()
    const { items, loading, isOpen } = useSelector<ApplicationState, WhatsNewState>(
        state => state.whatsNew
    )

    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)
    const sentinelRef = useRef<HTMLDivElement>(null)
    const bodyRef = useRef<HTMLDivElement>(null)
    const drawerRef = useRef<HTMLDivElement>(null)
    const previouslyFocused = useRef<HTMLElement | null>(null)

    const close = useCallback(() => {
        dispatch(setWhatsNewOpen(false))
    }, [dispatch])

    // While closed the drawer is still mounted (offscreen). Mark it inert so its close
    // button and feed links are not keyboard-focusable and are hidden from assistive tech.
    // Defined before the focus effect so inert is cleared before focus moves into the drawer.
    useEffect(() => {
        drawerRef.current?.toggleAttribute("inert", !isOpen)
    }, [isOpen])

    // Focus management: while open, move focus into the drawer and trap Tab within it;
    // on close, restore focus to the element that opened it (the nav button).
    useEffect(() => {
        if (!isOpen) return undefined
        previouslyFocused.current = document.activeElement as HTMLElement | null

        const focusables = (): HTMLElement[] => {
            const el = drawerRef.current
            if (!el) return []
            return Array.from(
                el.querySelectorAll<HTMLElement>(
                    'a[href], button, textarea, input, select, [tabindex]:not([tabindex="-1"])'
                )
            ).filter(n => !n.hasAttribute("disabled"))
        }

        focusables()[0]?.focus()

        const handler = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                close()
                return
            }
            if (e.key !== "Tab") return
            const f = focusables()
            if (f.length === 0) return
            const first = f[0]
            const last = f[f.length - 1]
            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault()
                last.focus()
            } else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault()
                first.focus()
            }
        }
        document.addEventListener("keydown", handler)
        return () => {
            document.removeEventListener("keydown", handler)
            previouslyFocused.current?.focus()
        }
    }, [isOpen, close])

    // Reset scroll position and visible count on open
    useEffect(() => {
        if (isOpen) {
            setVisibleCount(PAGE_SIZE)
            if (bodyRef.current) bodyRef.current.scrollTop = 0
        }
    }, [isOpen])

    // IntersectionObserver for infinite scroll sentinel
    useEffect(() => {
        const sentinel = sentinelRef.current
        if (!sentinel) return undefined
        const observer = new IntersectionObserver(
            entries => {
                if (entries[0].isIntersecting) {
                    setVisibleCount(c => c + PAGE_SIZE)
                }
            },
            { threshold: 0.1 }
        )
        observer.observe(sentinel)
        return () => observer.disconnect()
    }, [isOpen, items.length])

    // Sort: pinned first, then by publishedAt desc; then cap to the configured maximum.
    // Coerce unparseable dates (NaN) to 0 so the comparator stays consistent.
    const sorted: NewsFeedItem[] = [...items].sort((a, b) => {
        if (a.pinned && !b.pinned) return -1
        if (!a.pinned && b.pinned) return 1
        return (new Date(b.publishedAt).getTime() || 0) - (new Date(a.publishedAt).getTime() || 0)
    })
    const capped = sorted.slice(0, MAX_FEED_ITEMS)

    const visible = capped.slice(0, visibleCount)
    const hasMore = visibleCount < capped.length

    return (
        <div className={`whats-new-overlay${isOpen ? " whats-new-overlay--open" : ""}`}
             onClick={close}
             aria-hidden={!isOpen}
        >
            <div
                ref={drawerRef}
                className={`whats-new-drawer${isOpen ? " whats-new-drawer--open" : ""}`}
                onClick={e => e.stopPropagation()}
                role="dialog"
                aria-label="What's New"
                aria-modal="true"
            >
                <div className="whats-new-drawer__header">
                    <div className="whats-new-drawer__heading">
                        <h2 className="whats-new-drawer__title">What's New</h2>
                        <p className="whats-new-drawer__subtitle">
                            Stay up to date with the latest from PacBio.
                        </p>
                    </div>
                    <button
                        className="whats-new-drawer__close"
                        onClick={close}
                        aria-label="Close What's New panel"
                    >
                        ×
                    </button>
                </div>

                <div className="whats-new-drawer__body" ref={bodyRef}>
                    {loading && <WhatsNewSkeletons />}

                    {!loading && visible.length === 0 && (
                        <div className="whats-new-drawer__empty">No updates available.</div>
                    )}

                    {!loading && visible.map(item => (
                        <WhatsNewFeedItem key={item.id} item={item} />
                    ))}

                    {hasMore && <div className="whats-new-drawer__sentinel" ref={sentinelRef} />}
                </div>
            </div>
        </div>
    )
}
